import React, { useState, useEffect } from 'react';
import { Search, AlertTriangle, Package } from 'lucide-react';
import api from '../api';
import StatusBadge from '../components/StatusBadge';

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [alertFilter, setAlertFilter] = useState('');
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (brandFilter) params.set('brand', brandFilter);
      if (alertFilter) params.set('alert', alertFilter);
      params.set('page', page);
      params.set('limit', 40);
      const result = await api.inventory(params.toString());
      setItems(result.items || []);
      setTotal(result.total || 0);
      setSummary(result.summary || {});
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => {
    api.brands().then(b => setBrands(b || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [page, search, brandFilter, alertFilter]);

  return (
    <div>
      {/* Summary KPIs */}
      <div className="kpi-grid" style={{marginBottom:'20px'}}>
        <div className="kpi-card">
          <div className="kpi-label">Críticos</div>
          <div className="kpi-value danger">{summary.critical || 0}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Stock Bajo</div>
          <div className="kpi-value warning">{summary.low || 0}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Sobrestock</div>
          <div className="kpi-value" style={{color:'hsl(35,92%,45%)'}}>{summary.overstock || 0}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Normal</div>
          <div className="kpi-value success">{summary.normal || 0}</div>
        </div>
      </div>

      <div className="data-table-container">
        <div className="data-table-header">
          <h3>{total} productos</h3>
          <div className="data-table-actions">
            <div className="search-input">
              <Search size={15} />
              <input placeholder="Buscar..." data-testid="inventory-search-input" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="filter-select" value={brandFilter} onChange={e => { setBrandFilter(e.target.value); setPage(1); }}>
              <option value="">Todas las marcas</option>
              {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
            <select className="filter-select" value={alertFilter} onChange={e => { setAlertFilter(e.target.value); setPage(1); }}>
              <option value="">Todos los estados</option>
              <option value="CRITICO">Crítico</option>
              <option value="BAJO">Bajo</option>
              <option value="SOBRESTOCK">Sobrestock</option>
              <option value="NORMAL">Normal</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><Package size={48} /><h3>Sin datos de inventario</h3><p>Genera datos de ejemplo o carga un archivo de inventario.</p></div>
        ) : (
          <>
            <table className="data-table" data-testid="inventory-coverage-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Marca</th>
                  <th>ABC</th>
                  <th className="right">Actual</th>
                  <th className="right">Tránsito</th>
                  <th className="right">Neto</th>
                  <th className="right">Min</th>
                  <th className="right">Max</th>
                  <th className="right">Cobertura</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.product_id}>
                    <td>
                      <div style={{fontWeight:500,fontSize:'0.8rem'}}>{item.name}</div>
                      <div style={{fontSize:'0.7rem',color:'hsl(var(--muted-foreground))'}}>{item.sku} | {item.viscosity} {item.presentation}</div>
                    </td>
                    <td style={{fontSize:'0.78rem'}}>{item.brand}</td>
                    <td><span className={`abc-badge ${item.abc_class}`}>{item.abc_class}</span></td>
                    <td className="right mono">{item.current_stock}</td>
                    <td className="right mono">{item.in_transit}</td>
                    <td className="right mono" style={{fontWeight:600}}>{item.net_available}</td>
                    <td className="right mono">{item.min_stock}</td>
                    <td className="right mono">{item.max_stock}</td>
                    <td className="right">
                      <span style={{fontVariantNumeric:'tabular-nums',fontWeight:500}}>{item.coverage_days < 999 ? `${item.coverage_days.toFixed(0)}d` : '∞'}</span>
                    </td>
                    <td data-testid="inventory-alerts-badge"><StatusBadge status={item.alert} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>&lt;</button>
              <span style={{fontSize:'0.78rem',padding:'0 8px'}}>Página {page} de {Math.ceil(total / 40) || 1}</span>
              <button disabled={page >= Math.ceil(total / 40)} onClick={() => setPage(p => p + 1)}>&gt;</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
